import { useEffect, useRef, useCallback } from "react";
import { toast } from "../components/Toast";
import {
  IDLE_TIMEOUT_MS,
  BACKGROUND_RELOGIN_MS,
  ABSOLUTE_SESSION_MAX_MS,
  ACTIVITY_THROTTLE_MS,
  IDLE_CHECK_MS,
  HEARTBEAT_MS,
  SESSION_START_KEY,
  LAST_ACTIVITY_KEY,
  HEARTBEAT_KEY,
  LOGOUT_BROADCAST_KEY,
  readTimestamp,
  writeTimestamp,
} from "../auth/sessionConstants";

function getSessionStartMs() {
  try {
    const raw = sessionStorage.getItem(SESSION_START_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * - Logout após inatividade (separador visível).
 * - Logout ao regressar após ausência prolongada em segundo plano.
 * - Limite absoluto de tempo de sessão (sessionStorage).
 * - Logout noutro separador invalida este (localStorage).
 *
 * A inatividade conta-se pela hora real (última actividade gravada), e não por
 * um temporizador: os temporizadores param quando o computador adormece e
 * recomeçam do zero quando o navegador reabre os separadores.
 */
export function useSessionSecurity({ user, ready, logout, onRemoteLogout }) {
  const lastThrottleRef = useRef(0);
  const hiddenAtRef = useRef(null);

  const checkAbsoluteLimit = useCallback(() => {
    if (!user) return false;
    const start = getSessionStartMs();
    if (start == null) return false;
    if (Date.now() - start > ABSOLUTE_SESSION_MAX_MS) {
      toast("Sessão expirada (limite de segurança). Faça login novamente.", "error");
      logout();
      return true;
    }
    return false;
  }, [user, logout]);

  /** Verifica a inatividade pela hora real (só com o separador à vista). */
  const checkIdle = useCallback(() => {
    if (!user) return false;
    if (checkAbsoluteLimit()) return true;
    if (document.visibilityState !== "visible") return false;
    const last = readTimestamp(LAST_ACTIVITY_KEY);
    if (last != null && Date.now() - last >= IDLE_TIMEOUT_MS) {
      toast("Sessão terminada por inatividade (10 minutos). Faça login novamente.", "error");
      logout();
      return true;
    }
    return false;
  }, [user, logout, checkAbsoluteLimit]);

  const onActivity = useCallback(() => {
    if (!user) return;
    const now = Date.now();
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottleRef.current = now;
    // Antes de contar esta actividade: o computador pode ter acordado agora de horas parado.
    if (checkIdle()) return;
    writeTimestamp(LAST_ACTIVITY_KEY, now);
  }, [user, checkIdle]);

  useEffect(() => {
    if (!ready || !user) return undefined;

    if (checkIdle()) return undefined;
    if (readTimestamp(LAST_ACTIVITY_KEY) == null) writeTimestamp(LAST_ACTIVITY_KEY);
    writeTimestamp(HEARTBEAT_KEY);

    const idleTimer = setInterval(checkIdle, IDLE_CHECK_MS);
    const heartbeat = setInterval(() => writeTimestamp(HEARTBEAT_KEY), HEARTBEAT_MS);

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt != null && Date.now() - hiddenAt >= BACKGROUND_RELOGIN_MS) {
        toast("Por segurança, inicie sessão novamente após ausência prolongada.", "error");
        logout();
        return;
      }
      if (checkAbsoluteLimit()) return;
      writeTimestamp(HEARTBEAT_KEY);
      // Voltar ao separador (menos de 1 h fora) conta como actividade.
      if (hiddenAt != null) writeTimestamp(LAST_ACTIVITY_KEY);
      checkIdle();
    };

    document.addEventListener("visibilitychange", onVisibility);

    const onStorage = (e) => {
      if (e.key !== LOGOUT_BROADCAST_KEY || e.newValue == null || !user) return;
      onRemoteLogout();
    };
    window.addEventListener("storage", onStorage);

    const onPageShow = (ev) => {
      if (ev.persisted && user) checkIdle();
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      clearInterval(idleTimer);
      clearInterval(heartbeat);
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [ready, user, onActivity, checkIdle, logout, onRemoteLogout, checkAbsoluteLimit]);

  return null;
}
